import { CalendarDate, parseZonedDateTime } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  enumSalesLoadLineItemPhase,
  LoadSortOption,
  LoadStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';

import {
  BrokeredLoadLineItemRecord,
  BrokeredLoadRecord,
  enumBrokeredLoadWeightType,
} from '../types/brokeredLoad';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

const calculateLineItemsTotal = (
  lineItems: Partial<BrokeredLoadLineItemRecord>[],
): number =>
  lineItems.reduce((acc, lineItem) => {
    const netWeight =
      lineItem.weights?.[enumBrokeredLoadWeightType.Net]?.value ?? 0;
    const unitPrice = lineItem.unitPrice?.amount ?? 0;
    return acc + netWeight * unitPrice;
  }, 0);

export const brokeredLoadsListQueryAtom = createQueryAtomFactory(
  'brokerage.brokeredLoadsList',
  (params: {
    ids?: string[];
    status?: LoadStatus[];
    searchTerm?: string;
    fromExpectedShipDate?: CalendarDate;
    toExpectedShipDate?: CalendarDate;
    facilityId?: string;
    facilityIds?: string[];
    supplierIds?: string[];
    fromColumnDate?: CalendarDate;
    toColumnDate?: CalendarDate;
    columnStatuses?: LoadStatus[];
    limit?: number;
    offset?: number;
    sorting?: LoadSortOption[];
  }) => ({
    loads: {
      __args: {
        filter: {
          ids: params.ids,
          status: params.columnStatuses || params.status,
          searchTerm: params.searchTerm || undefined,
          supplierIds: params.supplierIds,
          hasSupplier: true,
          fromExpectedShipDate: (
            params.fromColumnDate || params.fromExpectedShipDate
          )?.toString(),
          toExpectedShipDate: (
            params.toColumnDate || params.toExpectedShipDate
          )?.toString(),
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        status: true,
        reviewed: true,
        expectedShipDate: true,
        shippedDate: true,
        documentNumber: true,
        supplierId: true,
        supplier: {
          id: true,
          name: true,
        },
        brokeredOrderId: true,
        brokeredOrder: {
          id: true,
          orderNumber: true,
        },
        salesOrderId: true,
        salesOrder: {
          id: true,
          documentNumber: true,
          account: {
            name: true,
          },
        },
        booking: {
          documentNumber: true,
        },
        lineItems: {
          id: true,
          itemId: true,
          item: {
            id: true,
            name: true,
          },
          purchaseOrderId: true,
          purchaseOrder: {
            id: true,
            documentNumber: true,
          },
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          pricingUnit: true,
          phase: true,
          weights: true,
        },
        facilityId: true,
        containerNumber: true,
        releaseNumber: true,
        timestamps: {
          createdAt: true,
        },
      },
    },
  }),
  {
    select: ({
      loads,
    }): {
      total: number;
      loads: BrokeredLoadRecord[];
    } => ({
      loads: loads.entries.map((load) => {
        const shippedLineItems = load.lineItems.filter(
          (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
        );

        const requestedLineItems = load.lineItems.filter(
          (item) =>
            item.phase === enumSalesLoadLineItemPhase.Requested || !item.phase,
        );

        const relevantLineItems = shippedLineItems?.length
          ? shippedLineItems
          : requestedLineItems;

        const netQuantity = relevantLineItems.reduce(
          (acc, lineItem) =>
            acc +
            Number(
              lineItem.weights?.[enumBrokeredLoadWeightType.Net]?.value ?? 0,
            ),
          0,
        );

        const shippedTotal = calculateLineItemsTotal(
          shippedLineItems as unknown as Partial<BrokeredLoadLineItemRecord>[],
        );

        return {
          id: load.id,
          loadNumber: load.documentNumber.toString(),
          status: load.status as LoadStatus,
          reviewed: load.reviewed ?? false,
          expectedShipDate: maybeParseCalendarDate(load.expectedShipDate),
          shippedDate: maybeParseCalendarDate(load.shippedDate),
          salesOrderId: load.salesOrderId ?? load.salesOrder?.id ?? undefined,
          salesOrderNumber: load.salesOrder?.documentNumber ?? undefined,
          bookingNumber: load.booking?.documentNumber ?? undefined,
          facilityId: load.facilityId ?? undefined,
          supplierId: load.supplierId as string,
          supplier: load.supplier as { id: string; name: string },
          brokeredOrderId: load.brokeredOrderId ?? undefined,
          brokeredOrder: load.brokeredOrder ?? undefined,
          containerNumber: load.containerNumber ?? undefined,
          releaseNumber: load.releaseNumber ?? undefined,
          customerName: load.salesOrder?.account?.name ?? undefined,
          shippedTotal,
          lineItems: load.lineItems.map((lineItem) => ({
            id: lineItem.id,
            itemId: lineItem.itemId ?? null,
            type: 'Raw' as const,
            loadId: null,
            item: lineItem.item ?? null,
            purchaseOrderId: lineItem.purchaseOrderId ?? null,
            purchaseOrder: lineItem.purchaseOrder ?? null,
            unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
            pricingUnit:
              lineItem.pricingUnit as BrokeredLoadLineItemRecord['pricingUnit'],
            phase: lineItem.phase,
            weights: lineItem.weights as Record<string, Weight>,
          })),
          lineItemCount: load.lineItems.length,
          netRequestedQuantity: requestedLineItems.reduce(
            (acc, lineItem) =>
              acc +
              Number(
                lineItem.weights?.[enumBrokeredLoadWeightType.Net]?.value ?? 0,
              ),
            0,
          ),
          netQuantity,
          timestamps: {
            createdAt: parseZonedDateTime(load.timestamps.createdAt),
          },
        };
      }),
      total: loads.page.total ?? 0,
    }),
  },
);
